// Mini Konva stage that renders a template's shapes shrunk-to-fit into a
// thumbnail card. We use the actual ShapeRenderer so what the picker shows
// matches what the user lands on after clicking — no separate visual stub
// that can drift from the renderer.

import { Layer, Stage } from 'react-konva';
import { ShapeRenderer } from '../../canvas/ShapeRenderer';
import { shapeBBox } from '../../canvas/binding/shapesMap';
import type { Template } from '@frame/shared';

interface Props {
  template: Template;
  width: number;
  height: number;
}

const PADDING = 16;

export function TemplateThumbnail({ template, width, height }: Props) {
  const { scale, dx, dy } = computeFit(template, width, height);
  return (
    <Stage
      width={width}
      height={height}
      x={dx}
      y={dy}
      scaleX={scale}
      scaleY={scale}
      listening={false}
    >
      <Layer listening={false}>
        {template.shapes.map((shape) => (
          <ShapeRenderer key={shape.id} shape={shape} />
        ))}
      </Layer>
    </Stage>
  );
}

// Compute the transform that fits a template's union bbox into `width × height`
// with PADDING on every side. Empty templates render at scale=1 origin=0,0.
function computeFit(template: Template, width: number, height: number): {
  scale: number;
  dx: number;
  dy: number;
} {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let found = false;
  for (const shape of template.shapes) {
    const bbox = shapeBBox(shape);
    if (!bbox) continue;
    found = true;
    if (bbox.x < minX) minX = bbox.x;
    if (bbox.y < minY) minY = bbox.y;
    if (bbox.x + bbox.w > maxX) maxX = bbox.x + bbox.w;
    if (bbox.y + bbox.h > maxY) maxY = bbox.y + bbox.h;
  }
  if (!found) return { scale: 1, dx: 0, dy: 0 };
  const contentW = Math.max(1, maxX - minX);
  const contentH = Math.max(1, maxY - minY);
  const scale = Math.min(
    (width - 2 * PADDING) / contentW,
    (height - 2 * PADDING) / contentH,
  );
  const dx = PADDING + (width - 2 * PADDING - contentW * scale) / 2 - minX * scale;
  const dy = PADDING + (height - 2 * PADDING - contentH * scale) / 2 - minY * scale;
  return { scale, dx, dy };
}
